import { Component, OnInit } from "@angular/core";
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { UntypedFormArray } from "@angular/forms";
import { Router } from "@angular/router";
import { FeatureViews } from "src/app/ui/models/domains/features/custom-feature-views";
import { PageViews } from "src/app/ui/models/domains/features/custom-page-views";
import { Pricing } from "src/app/ui/models/domains/pbm/product-management/pricing";
import { PricingInfoViewItemFactory } from "src/app/ui/models/domains/pbm/product-management/pricing-info-view-item.factory";
import { PricingInfoViewItem } from "src/app/ui/models/domains/pbm/product-management/pricing-info-view-item.model";
import { PricingQuotaConfiguration } from "src/app/ui/models/domains/pbm/product-management/pricing-quota-configuration.model";
import { PricingStateViewItem } from "src/app/ui/models/domains/pbm/product-management/pricing-state-view-item.model";
import { ProductLabelInfo } from "src/app/ui/models/domains/pbm/product-management/product-label-info";
import { ProductState } from "src/app/ui/models/domains/pbm/product-management/product-state.model";
import { UserPermissions } from "src/app/ui/models/domains/pbm/user/user-permissions";
import { RoutePath } from "src/app/ui/models/domains/route-paths.model";
import { FeatureViewService } from "src/app/ui/services/feature-view.service";
import { LoadingService } from "src/app/ui/services/loading.service";
import { ProductManagementService } from "src/app/ui/services/pbm/product-management.service";
import { UserService } from "src/app/ui/services/user.service";
import { Toast } from "../../../toast/toast";
import { ConfirmDialog } from "../../shared/dialogs/confirm-dialog/confirm-dialog.component";
import { MatDialogRef, MatDialog } from "@angular/material/dialog";

@Component({
  selector: "edit-product-pricing",
  templateUrl: "./edit-product-pricing.component.html",
  styleUrls: ["./edit-product-pricing.component.scss"],
})
export class EditProductPricingComponent implements OnInit {
  public quotaForms: UntypedFormArray;
  public productLabelInfo: ProductLabelInfo;
  private pricingStateViewItems: Array<PricingStateViewItem>;
  private hasPrincingConfigurated: boolean;
  public pricingsLastModified: string; // é a mesma data em todos os preços

  constructor(
    private productManagementService: ProductManagementService,
    private featureViewService: FeatureViewService,
    private loadingService: LoadingService,
    private formBuilder: UntypedFormBuilder,
    private toast: Toast,
    private router: Router,
    private userService: UserService,
    private confirmDialogRef: MatDialogRef<ConfirmDialog>,
    private dialog: MatDialog,
  ) {
    this.pricingStateViewItems = new Array<PricingStateViewItem>();
    this.quotaForms = new UntypedFormArray([]);
  }

  ngOnInit() {
    this.featureViewService.activeFeatureView(FeatureViews.EditProductPricing, PageViews.ProductManagementPageView);
    this.getPricings();
  }

  private async getPricings(): Promise<void> {
    const loadingRef = this.loadingService.beginLoading();
    try {
      const product = this.productManagementService.getPricingProduct();
      if (product && Object.keys(product).length) {
        this.productLabelInfo = product;

        const pricings = await this.productManagementService.productPricings(product.getProductKey());

        if (pricings.length > 0) {
          this.listExistingPrices(pricings);
        } else {
          this.createPricesFromTemplate();
        }
        this.sortPricingStateViewItems();
      } else {
        this.router.navigate([RoutePath.SearchProduct]);
      }
    } catch (error) {
      this.toast.showWarningToast("Houve um problema ao buscar os Preços, tente novamente mais tarde!");
      console.error(error);
    } finally {
      loadingRef.closeLoading();
    }
  }

  private listExistingPrices(pricings: Array<Pricing>) {
    this.hasPrincingConfigurated = true;
    this.pricingsLastModified = pricings[0].getModified(); // é a mesma data em todos os preços
    pricings.map((price) => {
      const pricingInfoViewItem = PricingInfoViewItemFactory.create(price);
      this.pushQuotaAndInfoViewItem(pricingInfoViewItem);
    });
  }

  private async createPricesFromTemplate() {
    const templates = await this.productManagementService.productPrincingTemplate();
    templates.map((templateItem, idxTemplate) => {
      const pricingQuotaConfiguration = new PricingQuotaConfiguration();
      pricingQuotaConfiguration.setState(templateItem.getState());
      pricingQuotaConfiguration.setQuota(templateItem.getQuota());
      pricingQuotaConfiguration.setFreeCommercialArea(templateItem.getFreeCommercialArea());
      const newPricingInfoViewItem = new PricingInfoViewItem();
      newPricingInfoViewItem.setPricingKey(`new#${idxTemplate}`); //preço ainda não existe no banco mas é preciso uma referência para exibir na listagem
      newPricingInfoViewItem.setPrice(0);
      newPricingInfoViewItem.setMaxPrice(0);
      newPricingInfoViewItem.setPricingQuotaConfiguration(pricingQuotaConfiguration);
      this.pushQuotaAndInfoViewItem(newPricingInfoViewItem);
    });
  }

  pushQuotaAndInfoViewItem(pricingInfoViewItem: PricingInfoViewItem) {
    this.buildFormGroup(pricingInfoViewItem);

    const stateIndex = this.pricingStateViewItems.findIndex(
      (state) => state.getProductState().getStateKey() === pricingInfoViewItem.getPricingQuotaConfiguration().getState().getStateKey(),
    );
    if (stateIndex != -1) {
      // já existe na lista de estados
      this.pricingStateViewItems[stateIndex].addPricingInfoViewItem(pricingInfoViewItem);
    } else {
      const newPricingStateViewItem = new PricingStateViewItem();
      newPricingStateViewItem.setProductState(pricingInfoViewItem.getPricingQuotaConfiguration().getState());
      newPricingStateViewItem.addPricingInfoViewItem(pricingInfoViewItem);
      this.pricingStateViewItems.push(newPricingStateViewItem);
    }
  }

  private buildFormGroup(pricingInfoViewItem: PricingInfoViewItem): void {
    const formGroup: UntypedFormGroup = this.formBuilder.group({
      pricingKey: new UntypedFormControl(pricingInfoViewItem.getPricingKey(), Validators.compose([Validators.required])),
      maxPrice: new UntypedFormControl(pricingInfoViewItem.getMaxPrice(), Validators.compose([Validators.required, Validators.min(0)])),
      price: new UntypedFormControl(pricingInfoViewItem.getPrice(), Validators.compose([Validators.required, Validators.min(0)])),
      quota: new UntypedFormControl(pricingInfoViewItem.getPricingQuotaConfiguration().getQuota(), Validators.compose([Validators.required])),
      stateId: new UntypedFormControl(pricingInfoViewItem.getPricingQuotaConfiguration().getState().getStateKey(), Validators.compose([Validators.required])),
      freeCommercialArea: new UntypedFormControl(pricingInfoViewItem.getPricingQuotaConfiguration().isFreeCommercialArea()),
      created: new UntypedFormControl(pricingInfoViewItem.getCreated()),
      modified: new UntypedFormControl(pricingInfoViewItem.getModified()),
    });
    this.quotaForms.push(formGroup);
  }

  public getFormGroup(pricingKey: string): AbstractControl {
    const index = this.quotaForms.controls.findIndex((item) => item.value["pricingKey"] === pricingKey);
    if (index == -1) {
      throw new Error("Formulário Inválido");
    }
    return this.quotaForms.controls[index];
  }

  public hasPricingStateViewItems(): boolean {
    return this.pricingStateViewItems && this.pricingStateViewItems.length > 0;
  }

  public getPricingStateViewItems(): Array<PricingStateViewItem> {
    return this.pricingStateViewItems;
  }

  public sortPricingStateViewItems() {
    this.pricingStateViewItems = this.pricingStateViewItems.sort((a, b) => {
      var compareA = a.getProductState().getName().toUpperCase();
      var compareB = b.getProductState().getName().toUpperCase();
      return compareA < compareB ? -1 : compareA > compareB ? 1 : 0;
    });
    // this.pricingStateViewItems.forEach((item) => {
    //   item.setPricingInfoViewItems(
    //     item.getPricingInfoViewItems().sort((a, b) => {
    //       var compareA = a.getPricingQuotaConfiguration().getQuota();
    //       var compareB = b.getPricingQuotaConfiguration().getQuota();
    //       return compareA < compareB ? -1 : compareA > compareB ? 1 : 0;
    //     })
    //   );
    // });
  }

  public hasProductLabelInfo(): boolean {
    return !!this.productLabelInfo;
  }

  public getProductLabelInfo(): ProductLabelInfo {
    return this.productLabelInfo;
  }

  public async backToProductEdit() {
    this.backToPage(RoutePath.EditProduct);
  }

  public backToSearchProduct(): void {
    this.backToPage(RoutePath.SearchProduct);
  }

  public async backToPage(rout: string) {
    let hasChanged = false;
    this.pricingStateViewItems.forEach((state) => {
      if (state.hasChanged()) {
        hasChanged = true;
      }
    });

    if (hasChanged) {
      this.confirmDialogRef = this.dialog.open(ConfirmDialog, {
        disableClose: true,
        hasBackdrop: true,
        height: "230px",
        width: "600px",
        data: {
          title: "Voltar Sem Gravar",
          text: "Houveram alterações nos preços! Os dados não serão gravados. Deseja realmente voltar?",
        },
      });
      this.confirmDialogRef.componentInstance.confirmDialogEvent.subscribe(async () => {
        this.router.navigate([rout]);
      });
    } else {
      this.router.navigate([rout]);
    }
  }

  public async savePrices(): Promise<void> {
    const loadingRef = this.loadingService.beginLoading();
    if (this.quotaForms.valid) {
      const productKey = this.productManagementService.getPricingProduct().getProductKey();
      const newPrices: PricingInfoViewItem[] = [];
      for (let item of this.quotaForms.controls) {
        const pricingInfoViewItem = new PricingInfoViewItem();
        let pricingKey = null;
        if ((item.get("pricingKey").value as string).split("#")[0] !== "new") {
          // se for o primeiro insert, vai mudar de 'new#...' para null
          pricingKey = item.get("pricingKey").value;
        }
        pricingInfoViewItem.setPricingKey(pricingKey);
        pricingInfoViewItem.setMaxPrice(item.get("maxPrice").value);
        pricingInfoViewItem.setPrice(item.get("price").value);

        const pricingQuotaConfiguration = new PricingQuotaConfiguration();
        pricingQuotaConfiguration.setQuota(item.get("quota").value);
        pricingQuotaConfiguration.setFreeCommercialArea(item.get("freeCommercialArea").value);
        const productState = new ProductState();
        productState.setStateKey(item.get("stateId").value);
        pricingQuotaConfiguration.setState(productState);
        pricingInfoViewItem.setPricingQuotaConfiguration(pricingQuotaConfiguration);

        if (pricingInfoViewItem.getMaxPrice() !== 0 && pricingInfoViewItem.getPrice() > pricingInfoViewItem.getMaxPrice()) {
          this.toast.showWarningToast("Atenção! Ao informar o Preço Máximo ele deve ser maior ou igual ao Preço.");
          loadingRef.closeLoading();
          return null;
        }
        newPrices.push(pricingInfoViewItem);
      }
      try {
        if (this.hasPrincingConfigurated) {
          await this.productManagementService.updateProductPricing(productKey, newPrices);
        } else {
          await this.productManagementService.insertProductPrincing(productKey, newPrices);
          this.hasPrincingConfigurated = true;
        }
        this.toast.showSuccessToast("Preços atualizados com sucesso!");
        this.pricingStateViewItems.forEach((state) => {
          state.setChangedStatus(false);
        });

        this.backToProductEdit(); //tem que voltar a pagina para pegar os IDs gerados quando primeiro insert
      } catch {
        this.toast.showSuccessToast("Não foi possivel atualizar os preços, tente novamente mais tarde.");
      } finally {
        loadingRef.closeLoading();
      }
    } else {
      this.toast.showWarningToast("Preencha os dados corretamente!");
      loadingRef.closeLoading();
    }
  }

  public async removePricing(pricingKey: string, pricingIndex: number): Promise<void> {
    const loadingRef = this.loadingService.beginLoading();
    const formGroup = this.getFormGroup(pricingKey);
    try {
      const productKey = this.productManagementService.getPricingProduct().getProductKey();
      await this.productManagementService.deleteProductPricing(productKey, pricingKey);
      const index = this.pricingStateViewItems[pricingIndex].getPricingInfoViewItems().findIndex((item) => item.getPricingKey() === pricingKey);
      if (index != -1) {
        this.pricingStateViewItems[pricingIndex].getPricingInfoViewItems().splice(index, 1);
        const formGroupIndex = this.quotaForms.controls.indexOf(formGroup);
        this.quotaForms.controls.splice(formGroupIndex, 1);
      }
      this.toast.showSuccessToast("Configuração de preços removida.");
    } catch {
      this.toast.showWarningToast("Não foi possivel remover os preços, tente novamente mais tarde.");
    } finally {
      loadingRef.closeLoading();
    }
  }

  public hasAccessToEditProductPricing(): boolean {
    return this.userService.getLoggedUser().hasPermission(UserPermissions.EditProductPricing);
  }

  public onDataChange(state: PricingStateViewItem): void {
    state.setChangedStatus(true);
  }

  public resetPrices(pricing: PricingInfoViewItem): void {
    const form = this.getFormGroup(pricing.getPricingKey());
    form.patchValue({
      price: 0,
      maxPrice: 0,
    });
  }
}
